import { useEffect, useRef, useState } from 'react';

export interface OptionChoix {
  valeur: string;
  libelle: string;
}

/**
 * Liste deroulante a choix multiple, pour les barres de filtres.
 *
 * Une liste `select multiple` native n'etait pas tenable ici : elle
 * s'affiche depliee, prend la hauteur de ses options et casse la barre
 * de filtres d'une ligne. C'est donc un bouton qui ouvre un panneau de
 * cases a cocher.
 *
 * Le bouton dit TOUJOURS ce qui est retenu — le libelle seul quand rien
 * ne l'est, la valeur quand il n'y en a qu'une, « Jour (3) » au-dela.
 * Un filtre pose doit se voir sans etre ouvert, sinon on cherche une
 * ligne qu'un filtre oublie masque.
 */
export function ChoixMultiple({
  libelle,
  options,
  valeurs,
  onChanger,
  classe = 'w-44',
}: {
  libelle: string;
  options: OptionChoix[];
  valeurs: string[];
  onChanger: (valeurs: string[]) => void;
  /**
   * La largeur : `w-44` fixe par defaut (la meme pour tous, sans quoi la
   * barre change de decoupe a chaque coche) ; `w-full` dans une grille
   * qui repartit elle-meme la place (ecran Adherents, 2026-10-08).
   */
  classe?: string;
}) {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);

  // Un panneau qui ne se referme qu'en recliquant son bouton reste
  // ouvert quand on va cliquer ailleurs, et recouvre la liste.
  useEffect(() => {
    if (!ouvert) return;
    const auClic = (evenement: MouseEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  const basculer = (valeur: string) =>
    onChanger(
      valeurs.includes(valeur) ? valeurs.filter((v) => v !== valeur) : [...valeurs, valeur],
    );

  const resume =
    valeurs.length === 0
      ? libelle
      : valeurs.length === 1
        ? (options.find((o) => o.valeur === valeurs[0])?.libelle ?? libelle)
        : `${libelle} (${valeurs.length})`;

  return (
    <div className={`relative ${classe}`} ref={conteneur}>
      <button
        type="button"
        // Largeur FIXE, la meme pour tous : en `w-auto` chaque bouton se
        // dimensionnait sur son libelle, et la barre changeait de decoupe
        // des qu'on cochait une valeur. Le conteneur porte la largeur, le
        // bouton la remplit.
        className={`champ flex w-full items-center justify-between gap-2 ${
          valeurs.length ? 'border-encre-400 font-medium' : ''
        }`}
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
      >
        <span className="truncate">{resume}</span>
        <span aria-hidden className="shrink-0 text-xs text-encre-400">
          ▾
        </span>
      </button>

      {ouvert && (
        <div
          // Ancre a DROITE : les filtres sont en fin de barre, et un
          // panneau qui s'ouvrirait vers la droite sortirait de la
          // fenetre — constate sur « Activite », le dernier des quatre.
          className="absolute right-0 z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-md border border-encre-200 bg-white p-1 shadow-lg"
        >
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-encre-400">Aucune valeur</p>
          ) : (
            options.map((option) => (
              <label
                key={option.valeur}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-encre-700 hover:bg-encre-100"
              >
                <input
                  type="checkbox"
                  checked={valeurs.includes(option.valeur)}
                  onChange={() => basculer(option.valeur)}
                />
                {option.libelle}
              </label>
            ))
          )}
          {valeurs.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-encre-500 underline hover:bg-encre-100"
              onClick={() => onChanger([])}
            >
              Tout décocher
            </button>
          )}
        </div>
      )}
    </div>
  );
}
